import { Component, Vue } from 'vue-property-decorator';
import moment from 'moment';
import { DATE_FORMAT, DEFAULT_DISPLAY_VALUE, EVALUATION_STATUSES, PAGE_NAMES, NOTIFICATION } from '@/constants';
import { AssessmentPeriodDTO, DisplayStatus, ProjectDTO, TableHeaderColumns } from '@/models';
import { fetchAssessmentPeriods, fetchProjectEvaluation, putProjectEvaluation } from '@/services';
import { convertNumberToFourDigitsString } from '@/helpers';
import InlineLoader from '@/components/common/loader/inline-loader/InlineLoader.vue';
import SearchInput from '@/components/common/search-input/SearchInput.vue';
import Status from '@/components/common/status/Status.vue';

type ProjectInTable = Omit<ProjectDTO, 'id'> & { id: string };
@Component({
  components: {
    InlineLoader,
    SearchInput,
    Status
  }
})
export default class InitiateEvaluation extends Vue {
  private projects: ProjectDTO[] = [];
  private initialFetchedProject: ProjectDTO[] = [];
  private isProjectsFetching = true;
  private isEvaluationSending = false;
  private today: Date = new Date();
  private isConfirm = false;
  private isNoProjects = false;
  private headers: TableHeaderColumns = [
    { text: 'ID', align: 'start', value: 'id' },
    { text: 'Project Name', value: 'name' },
    { text: 'Start date', value: 'startDate' },
    { text: 'End date', value: 'endDate' },
    { text: 'Date in field', value: 'dateInField', align: 'center' },
    { text: 'Evaluation Status', value: 'projectStatus', align: 'center', sortable: false }
  ];
  private singleSelect = false;
  private selected: ProjectDTO[] = [];
  private search = '';
  private dialog = false;
  private page = 1;
  private pageCount = 0;
  private itemsPerPage = 10;
  private currentAssessmentPeriod = {} as AssessmentPeriodDTO;

  get displayTableData(): ProjectInTable[] {
    return this.projects.map(project => ({
      ...project,
      id: convertNumberToFourDigitsString(project.id)
    }));
  }

  async cancel(): Promise<void> {
    await this.$router.go(-1);
    this.isProjectsFetching = false;
  }

  displayID(id: number): string {
    return convertNumberToFourDigitsString(id);
  }

  displayDate(date: string): string {
    if (date) {
      return moment(date).format(DATE_FORMAT.DISPLAY);
    }
    return DEFAULT_DISPLAY_VALUE.DATE;
  }

  displayDateInField(dateInField: number): string {
    if (dateInField) {
      return dateInField.toString();
    }
    return DEFAULT_DISPLAY_VALUE.DATE;
  }

  async mounted(): Promise<void> {
    await fetchProjectEvaluation()
      .then(payload => {
        this.projects = payload.data || [];
        if (this.projects.length !== 0) {
          this.projects.map(item => {
            if (item.startDate) item.dateInField = this.getDateInField(item);
          });
          this.projects.sort((p1, p2) => p1.dateInField - p2.dateInField);
        } else {
          this.isNoProjects = true;
        }
        this.initialFetchedProject = [...this.projects];
      })
      .finally(() => {
        this.isProjectsFetching = false;
      });
    await fetchAssessmentPeriods().then((payload: { data: AssessmentPeriodDTO[] }) => {
      if (payload.data.length !== 0) {
        this.currentAssessmentPeriod = payload.data.filter(item => item.status === 'ON_GOING')[0];
      }
    });
  }

  async navigationCreateNewProject(): Promise<void> {
    this.$router.push({
      name: PAGE_NAMES.PROJECT_CREATION
    });
  }

  closeNoProjectsDialog(): void {
    this.isNoProjects = false;
    this.$router.go(-1);
  }

  nextPage(): void {
    this.page = this.page + 1;
  }

  prevPage(): void {
    this.page = this.page - 1;
  }

  confirm(): void {
    this.isConfirm = true;
    this.projects = this.selected;
  }

  back(): void {
    this.isConfirm = false;
    this.projects = [...this.initialFetchedProject];
  }

  async sendEvaluationRequest(): Promise<void> {
    this.isEvaluationSending = true;
    await putProjectEvaluation(this.selected.map((project: { id: number }) => project.id));
    this.isEvaluationSending = false;
    const data = {
      showing: true,
      type: NOTIFICATION.TYPE.success,
      title: NOTIFICATION.TEXT.initiateEvaluationSuccess,
      content: [
        'Assessment period: ' + this.currentAssessmentPeriod.name,
        'Number of projects: ' + this.selected.length
      ],
      icon: null,
      multiline: true,
      height: 0
    };
    this.$store.commit('notification/setNotification', data);
    this.confirmDialog();
  }

  confirmDialog(): void {
    this.dialog = false;
    this.isConfirm = false;
    this.selected = [];
    this.$router.go(-1);
  }

  getDateInField(item: ProjectDTO): number {
    let result = 0;
    if (this.today.getTime() > new Date(item.startDate).getTime()) {
      result = (this.today.getTime() - new Date(item.startDate).getTime()) / (24 * 3600 * 1000);
    }
    return parseInt(result.toString());
  }

  onSearch(searchText: string): void {
    this.search = searchText;
  }

  getDisplayStatus(status: string): DisplayStatus {
    return EVALUATION_STATUSES[status];
  }
}
